/* eslint-disable max-params */
/**
 * Utility helper for fetching company logos with fallback mechanism
 */
export class CompanyLogoHelper {
  private static readonly LOGO_DEV_PUBLIC_KEY = import.meta.env.VITE_LOGO_DEV_PUBLIC_KEY;

  public static getFallbackUrls(companyId?: number | string, name?: string, url?: string, stockTicker?: string): string[] {
    const urls: string[] = [];

    // 1: by domain
    if (url) {
      urls.push(this.getLogoServiceUrlByDomain(url));
    }
    // 2: by stock ticker
    if (stockTicker) {
      urls.push(this.getLogoServiceUrlByStockTicker(stockTicker));
    }
    // 3: custom upload
    if (companyId) {
      urls.push(`${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/logo`);
      urls.push(this.getNewS3LogoUrl(companyId));
    }

    return urls;
  }

  /**
   * Gets the new S3 bucket URL for company logos
   * @param companyId - Company ID
   * @returns string - New S3 bucket URL
   */
  private static getNewS3LogoUrl(companyId: number | string): string {
    const environment = this.getS3BucketEnvironmentName();
    return `https://kandir-images-${environment}.s3.us-west-1.amazonaws.com/company-logos/${companyId}.jpeg`;
  }

  private static getLogoServiceUrlByDomain(url: string): string {
    // Extract just the domain from the URL (logo.dev expects domain only, not full URL)
    let domain = url;
    try {
      // Handle URLs with or without protocol
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
      const parsed = new URL(urlWithProtocol);
      domain = parsed.hostname.replace(/^www\./, '');
    } catch {
      // If URL parsing fails, try simple string manipulation
      domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    }
    return `https://img.logo.dev/${domain}?fallback=404&token=${this.LOGO_DEV_PUBLIC_KEY}`;
  }

  private static getLogoServiceUrlByStockTicker(stockTicker?: string): string {
    return `https://img.logo.dev/ticker/${stockTicker}?fallback=404&token=${this.LOGO_DEV_PUBLIC_KEY}`;
  };

  /**
   * Gets the environment name for bucket naming
   * @returns string - Environment name (development, production)
   */
  private static getS3BucketEnvironmentName(): string {
    // Use Vite's environment variable to determine environment
    // Only use 'production' for production builds, everything else uses 'development'
    return import.meta.env.VITE_ENV === 'production'
      ? 'production'
      : 'development';
  }
}
